
import { useContext, useCallback } from 'react';
import { ContentManagerContext } from '@/state/content/ContentManagerProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useLoadingState } from '@/utils/loading';

export const useContentManager = () => {
  const context = useContext(ContentManagerContext);
  const queryClient = useQueryClient();
  const { startLoading, stopLoading } = useLoadingState();
  
  if (!context) {
    throw new Error('useContentManager must be used within a ContentManagerProvider');
  }

  // Enhanced navigation function that doesn't rely on useNavigate
  const navigateTo = useCallback((path: string, state?: Record<string, any>) => {
    // Start loading indicator for navigation
    const loadingId = startLoading(`navigation:${path}`, 'high');
    
    // Preload the content for this path
    context.preloadView(path);
    
    // Prefetch route data
    context.prefetchRouteData(path);
    
    // Navigate using the context method (works outside router context)
    context.navigateTo(path, state);
    
    // Stop loading after navigation (this will be fast since it's client-side)
    stopLoading(loadingId);
  }, [context, startLoading, stopLoading]);

  // Get the current view data
  const getCurrentViewData = useCallback(() => {
    return context.currentView?.data;
  }, [context.currentView]);

  // Get the current view state
  const getCurrentViewState = useCallback(() => {
    return context.currentView?.state || {};
  }, [context.currentView]);

  // Update state for the current view
  const updateCurrentViewState = useCallback((state: Record<string, any>) => {
    if (context.currentView?.path) {
      context.updateViewState(context.currentView.path, state);
    }
  }, [context]);

  // Check if a view is in the cache
  const isViewCached = useCallback((path: string) => {
    return !!context.cachedViews[path];
  }, [context.cachedViews]);

  // Get scroll position for a specific path
  const getScrollPosition = useCallback((path: string) => {
    return context.cachedViews[path]?.scrollPosition || 0;
  }, [context.cachedViews]);

  // Get state for a specific path
  const getStateForPath = useCallback((path: string) => {
    return context.getViewState(path);
  }, [context]);

  // Update state for a specific path
  const updateStateForPath = useCallback((path: string, state: Record<string, any>) => {
    context.updateViewState(path, state);
  }, [context]);

  // Optimized cache cleanup method
  const clearUserCache = useCallback(() => {
    // Clear user-specific cache segments
    context.invalidateSegment('user');
    context.invalidateSegment('auth');
    
    // Also clear context cache
    context.clearCache();
  }, [context]);

  return {
    ...context,
    navigateTo,
    getCurrentViewData,
    getCurrentViewState,
    updateCurrentViewState,
    isViewCached,
    getScrollPosition,
    getStateForPath,
    updateStateForPath,
    clearUserCache
  };
};
